import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, MessageCircle, UserMinus, UserPlus, X } from 'lucide-react';
import { AppUser, Listing } from '../../../types';
import { useSuperAdminActions } from '../../../hooks/useListingAdmin';
import { inviteErrorCode, useInviteActions, useListingInvites } from '../../../hooks/useInvites';
import type { MailStatus } from '../../../hooks/useListingEmails';
import { looksLikeEmail } from '../../../utils/superAdmin';
import { pickLang } from '../../../utils/listingDisplay';
import { Checkbox, INPUT_CLASS, SectionTitle } from '../listing/formFields';
import { formatDate, userLabel } from './superFormat';

const BUTTON = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 rounded-[10px] text-sm font-medium transition-opacity disabled:opacity-40';

/** Owners of one listing: remove, add an existing user, or send an invite link (plan 10.4). */
export default function OwnersPanel({ listing, users }: { listing: Listing; users: AppUser[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { addOwner, removeOwner, saving } = useSuperAdminActions();
  const [pick, setPick] = useState('');
  const [failed, setFailed] = useState(false);

  const owners = listing.ownerUids ?? [];
  const candidates = users.filter((u) => !owners.includes(u.uid));

  const act = async (fn: () => Promise<unknown>) => {
    setFailed(false);
    try {
      await fn();
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <SectionTitle>{t('directory.super.owners.title')}</SectionTitle>
        {owners.length === 0 ? (
          <p className="text-sm text-ash">{t('directory.super.owners.none')}</p>
        ) : (
          <ul className="space-y-1">
            {owners.map((uid) => {
              const u = users.find((x) => x.uid === uid);
              return (
                <li key={uid} className="flex items-center justify-between gap-2">
                  <bdi className="text-sm text-ink break-all">{u ? userLabel(u) : uid}</bdi>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      if (window.confirm(t('directory.super.owners.confirmRemove'))) act(() => removeOwner(listing, uid));
                    }}
                    className={`${BUTTON} text-danger-600 hover:underline shrink-0`}
                  >
                    <UserMinus className="w-4 h-4" aria-hidden />
                    {t('directory.super.owners.remove')}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            aria-label={t('directory.super.owners.pick')}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">{t('directory.super.owners.pick')}</option>
            {candidates.map((u) => (
              <option key={u.uid} value={u.uid}>
                {userLabel(u)}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!pick || saving}
            onClick={() => act(async () => {
              await addOwner(listing, pick);
              setPick('');
            })}
            className={`${BUTTON} bg-ink text-paper shrink-0`}
          >
            <UserPlus className="w-4 h-4" aria-hidden />
            {t('directory.super.owners.add')}
          </button>
        </div>
        {failed && <p role="alert" className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
      </div>

      <InviteSection listingId={listing.id} listingName={pickLang(listing.nameEn, listing.nameHe, isHebrew)} />
    </div>
  );
}

function InviteSection({ listingId, listingName }: { listingId: string; listingName: string }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const invites = useListingInvites(listingId);
  const { busy, createInvite, cancelInvite } = useInviteActions();
  const [email, setEmail] = useState('');
  const [link, setLink] = useState('');
  const [byEmail, setByEmail] = useState(true);
  const [emailNote, setEmailNote] = useState<{ status: MailStatus; email: string } | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setErrorKey(null);
    setLink('');
    setEmailNote(null);
    setCopied(false);
    if (!looksLikeEmail(email)) {
      setErrorKey('badEmail');
      return;
    }
    try {
      const result = await createInvite(email, listingId, byEmail);
      setLink(result.link);
      if (result.emailStatus) setEmailNote({ status: result.emailStatus, email: email.trim().toLowerCase() });
      setEmail('');
    } catch (err) {
      setErrorKey(inviteErrorCode(err) === 'permission-denied' ? 'notAllowed' : 'failed');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const whatsappText = t('directory.super.invite.whatsappText', { name: listingName, link });

  return (
    <div className="space-y-2">
      <SectionTitle>{t('directory.super.invite.title')}</SectionTitle>
      <p className="text-xs text-ash">{t('directory.super.invite.hint')}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          dir="ltr"
          inputMode="email"
          aria-label={t('directory.super.invite.email')}
          placeholder={t('directory.super.invite.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={INPUT_CLASS}
        />
        <button type="button" disabled={busy || !email.trim()} onClick={handleCreate} className={`${BUTTON} bg-ink text-paper shrink-0`}>
          {busy ? t('common.loading') : t('directory.super.invite.create')}
        </button>
      </div>
      <Checkbox label={t('directory.super.invite.sendByEmail')} checked={byEmail} onChange={setByEmail} />
      {errorKey && <p role="alert" className="text-sm text-danger-600">{t(`directory.super.invite.error.${errorKey}`)}</p>}

      {link && (
        <div className="rounded-[10px] border border-ink/20 bg-fog p-3 space-y-2">
          <p className="text-sm text-ink">{t('directory.super.invite.ready')}</p>
          {emailNote && (
            <p role="status" className={`text-sm ${emailNote.status === 'failed' ? 'text-danger-600' : 'text-ink'}`}>
              {emailNote.status === 'sent'
                ? t('directory.super.invite.emailSent', { email: emailNote.email })
                : emailNote.status === 'notSent'
                ? t('directory.super.invite.emailNotSent')
                : t('directory.super.invite.emailFailed')}
            </p>
          )}
          <input readOnly dir="ltr" value={link} onFocus={(e) => e.target.select()} className={INPUT_CLASS} aria-label={t('directory.super.invite.link')} />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={copy} className={`${BUTTON} border border-ink text-ink`}>
              <Copy className="w-4 h-4" aria-hidden />
              {copied ? t('directory.super.invite.copied') : t('directory.super.invite.copy')}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${BUTTON} border border-ink text-ink`}
            >
              <MessageCircle className="w-4 h-4" aria-hidden />
              {t('directory.super.invite.whatsapp')}
            </a>
          </div>
        </div>
      )}

      {invites.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-ash">{t('directory.super.invite.open')}</p>
          <ul className="space-y-1">
            {invites.map((inv) => {
              const expired = new Date(inv.expiresAt) < new Date();
              return (
                <li key={inv.token} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0">
                    <span className="block text-ink break-all" dir="ltr">{inv.email}</span>
                    <span className="block text-xs text-ash">
                      {expired
                        ? t('directory.super.invite.expired')
                        : t('directory.super.invite.until', { date: formatDate(inv.expiresAt, isHebrew) })}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(t('directory.super.invite.confirmCancel'))) cancelInvite(inv.token).catch(() => setErrorKey('failed'));
                    }}
                    className={`${BUTTON} text-ash hover:text-ink shrink-0`}
                  >
                    <X className="w-4 h-4" aria-hidden />
                    {t('directory.super.invite.cancel')}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
